import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';
import { LoadingScreen } from 'src/components/loading-screen';
import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  useTable,
} from 'src/components/table';
import { useBoolean } from 'src/hooks/use-boolean';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { getRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { fTimestamp } from 'src/utils/format-time';
import Header from '../header';
import OrderTableToolbar from './geenral-ledger-report-filter';
// import { getCsvData } from './getCsv_general_ledger';
import { CSVLink } from 'react-csv';
import UserTableRow from './cash-flow-table-row';
import { getCsvData } from "./getCsv_cash_flow";
// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'Sr.No', label: 'Sr.no' },
  { id: 'date', label: 'Date' },
  { id: 'bank_information', label: 'Bank Name' },
  { id: 'transaction_type', label: 'Transaction Type' },
  { id: 'source', label: 'Source' },
  { id: 'description', label: 'Description' },
  { id: 'expense', label: 'Expese' },
  { id: 'income', label: 'Income' },
  { id: 'balance', label: 'Balance($)' },
];

const defaultFilters = {
  name: '',
  status: 'all',
  startDate: null,
  endDate: null,
};

// ----------------------------------------------------------------------

export default function CashFlowReport() {
  const table = useTable({ defaultOrderBy: 'orderNumber' });
  const navigate = useNavigate();
  const [ loading, setLoading ] = useState(false);
  const router = useRouter();
  const confirm = useBoolean();
  const [ tableData, setTableData ] = useState([]);
  const [ filters, setFilters ] = useState(defaultFilters);

  const csvLinkRef = useRef();
  const dateError =
    filters.startDate && filters.endDate
      ? filters.startDate.getTime() > filters.endDate.getTime()
      : false;

  const dataFiltered = useMemo(() => {
    return applyFilter({
      inputData: tableData,
      comparator: getComparator(table.order, table.orderBy),
      filters,
      dateError,
    });
  }, [ tableData, table.order, table.orderBy, filters, dateError ]);

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 52 : 72;
  const canReset =
    !!filters.name || filters.status !== 'all' || (!!filters.startDate && !!filters.endDate);
  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [ name ]: value,
      }));
    },
    [ table ]
  );

  const handleDeleteRow = useCallback(
    (id) => {
      const deleteRow = tableData.filter((row) => row.id !== id);
      setTableData(deleteRow);

      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [ dataInPage.length, table, tableData ]
  );
  const handleDeleteRows = useCallback(() => {
    const deleteRows = tableData.filter((row) => !table.selected.includes(row.id));
    setTableData(deleteRows);

    table.onUpdatePageDeleteRows({
      totalRows: tableData.length,
      totalRowsInPage: dataInPage.length,
      totalRowsFiltered: dataFiltered.length,
    });
  }, [ dataFiltered.length, dataInPage.length, table, tableData ]);
  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);
  const handleViewRow = useCallback(
    (id) => {
      router.push(paths.dashboard.order.details(id));
    },
    [ router ]
  );
  const handleFilterStatus = useCallback(
    (event, newValue) => {
      handleFilters('status', newValue);
    },
    [ handleFilters ]
  );

  useEffect(() => {
    getCashFlowData();
  }, []);

  const getCsvHeaders = () => {
    return TABLE_HEAD.filter(({ id }) => id !== 'Sr.No')
      .map(({ id, label }) => ({
        label,
        key: id,
      }));
  };
  const handleExport = () => {
    if (csvLinkRef.current) {
      const csvData = getCsvData(dataFiltered);
      csvLinkRef.current.link.click(csvData);
    }
  };

  const getCashFlowData = async (projectId) => {
    try {
      setLoading(true);
      const endpoint = projectId && projectId !== 'All'
        ? `${endpoints.accounting.general_ledger.get_cash_flow}?project_id=${projectId}`
        : endpoints.accounting.general_ledger.get_cash_flow;
      const data = await getRequest(endpoint);
      if (data) {

        setTableData(data);
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      // Optionally, set an error state here to display to the user
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={ { width: '100%', padding: '20px' } }>
        <Header name="Cash Flow  Report" />

        <Box
          sx={ {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
          } }
        >
          <OrderTableToolbar
            filters={ filters }
            onFilters={ handleFilters }
            //
            //defaultStartDate={defaultStartDate}
            canReset={ canReset }
            onResetFilters={ handleResetFilters }
          />
          {/* Action Buttons */ }
          <Box sx={ { display: 'flex', gap: 1 } }>
            <Button
              className="dark-button"
              variant="contained"
              startIcon={ <Iconify icon="ic:baseline-email" /> }
            //onClick={handleOpenShareModal} // Updated to open modal and generate PDF
            >
              Share By mail
            </Button>
            <Button
              onClick={ handleExport }
              className="dark-button"
              variant="contained"
              startIcon={ <Iconify icon="solar:export-bold" /> }
            >
              Export
            </Button>
            <Button
              onClick={()=> navigate('/accounts-management/general-ledger/cash-flow') }
              className="dark-button"
              variant="contained"
              startIcon={ <Iconify icon="material-symbols:arrow-back-rounded" /> }
            >
              back
            </Button>

          </Box>
        </Box>

        { loading ? (
          <Box height={ 700 }>
            <LoadingScreen />
          </Box>
        ) : (<Card>
          <TableContainer sx={ { position: 'relative', overflow: 'unset' } }>
            <TableSelectedAction
              dense={ table.dense }
              numSelected={ table.selected.length }
              rowCount={ tableData.length }
              onSelectAllRows={ (checked) =>
                table.onSelectAllRows(
                  checked,
                  tableData.map((row) => row.id)
                )
              }
              action={
                <Tooltip title="Delete">
                  <IconButton color="primary" onClick={ confirm.onTrue }>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </Tooltip>
              }
            />

            <Scrollbar>
              <Table size={ table.dense ? 'small' : 'large' } sx={ { minWidth: "100%" } }>
                <TableHeadCustom
                  order={ table.order }
                  orderBy={ table.orderBy }
                  headLabel={ TABLE_HEAD }
                  rowCount={ tableData.length }
                  numSelected={ table.selected.length }
                  onSort={ table.onSort }
                  onSelectAllRows={ (checked) =>
                    table.onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                />
                <TableBody>
                  { dataFiltered
                    .slice(
                      table.page * table.rowsPerPage,
                      table.page * table.rowsPerPage + table.rowsPerPage
                    )
                    .map((row, index) => (
                      <UserTableRow
                        key={ row.id }
                        row={ row }
                        selected={ table.selected.includes(row.id) }
                        onSelectRow={ () => table.onSelectRow(row.id) }
                        onDeleteRow={ () => handleDeleteRow(row.id) }
                        serialNumber={ table.page * table.rowsPerPage + index + 1 }
                      />
                    )) }
                  <TableEmptyRows
                    height={ denseHeight }
                    emptyRows={ emptyRows(table.page, table.rowsPerPage, tableData.length) }
                  />
                  <TableNoData notFound={ notFound } />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        </Card>
        ) }
        {
          <CSVLink
            ref={ csvLinkRef }
            filename="Cash_flow_Report.csv"
            data={ getCsvData(tableData) }
            style={ { display: 'none' } }
          />
        }
        <TablePaginationCustom
          count={ dataFiltered.length }
          page={ table.page }
          rowsPerPage={ table.rowsPerPage }
          onPageChange={ table.onChangePage }
          onRowsPerPageChange={ table.onChangeRowsPerPage }
          dense={ table.dense }
          onChangeDense={ table.onChangeDense }
        />
      </Box>
      <ConfirmDialog
        open={ confirm.value }
        onClose={ confirm.onFalse }
        title="Delete"
        content={
          <>
            Are you sure want to delete <strong> { table.selected.length } </strong> items?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={ () => {
              handleDeleteRows();
              confirm.onFalse();
            } }
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
// ----------------------------------------------------------------------
function applyFilter({ inputData, comparator, filters, dateError }) {
  const { status, name, startDate, endDate } = filters;
  const stabilizedThis = inputData.map((el, index) => [ el, index ]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[ 0 ], b[ 0 ]);
    if (order !== 0) return order;
    return a[ 1 ] - b[ 1 ];
  });
  inputData = stabilizedThis.map((el) => el[ 0 ]);
  if (name) {
    inputData = inputData.filter(
      (order) =>
        order.orderNumber.toLowerCase().indexOf(name.toLowerCase()) !== -1 ||
        order.customer.name.toLowerCase().indexOf(name.toLowerCase()) !== -1 ||
        order.customer.email.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }
  if (status !== 'all') {
    inputData = inputData.filter((order) => order.status === status);
  }
  if (!dateError) {
    if (startDate && endDate) {
      const start = fTimestamp(startDate, true, false);
      const end = fTimestamp(endDate, false, true);
      let invoiceMatches = inputData.filter(order => {
        const invoiceDate = fTimestamp(order?.invoice_date);
        return invoiceDate >= start && invoiceDate <= end;
      });
      let receiptMatches = inputData.filter(order => {
        const receiptDate = fTimestamp(order?.date_of_receipt);
        return receiptDate >= start && receiptDate <= end;
      });
      let dateMatches = inputData.filter(order => {
        const date = fTimestamp(order?.date);
        return date >= start && date <= end;
      });
      const mergedResults = [ ...invoiceMatches, ...receiptMatches, ...dateMatches ];
      inputData = mergedResults;
    }
  }
  return inputData;
}